import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';

import { } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import _ from 'lodash';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { FormatDatasetAdapter } from 'app/model/adapters/datasets/format-dataset.adapter';
import { DelimitedTextDataset } from 'shell-models-lib';
import { string2UnicodeSingle, unicode2StrSingle } from 'app/shared/utils/string.util';
import { rowDelimiterV2Options, columnDelimiterV2Options, quoteCharV2Options, escapeCharV2Options } from 'app/shared/components/fileformat/fileformat.constants';
import { GlobalVariables } from 'app/services/globals';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(DelimitedTextDataset),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            folder: '*',
            annotations: '*',
            schema: '*',
            parameters: '*',
            type: '*',
            linkedServiceName: '*',
            linkedService: '*',
            connectionSettings: __IS_FABRIC_DPE__ ? '*' : undefined,
            connectionProperties: GlobalVariables.disableServerCertificate ? '*' : undefined,
            typeProperties: {
                location: '*',
                columnDelimiter: '*',
                rowDelimiter: '*',
                compressionCodec: '*',
                compressionLevel: '*',
                encodingName: '*',
                escapeChar: '*',
                firstRowAsHeader: '*',
                nullValue: '*',
                quoteChar: '*',
                multiline: '*'
            }
        }
    }
})
export class DelimitedTextDatasetAdapter extends FormatDatasetAdapter<DelimitedTextDataset, Model.DatasetV2Resource> {
    protected async _toResource(dataset: DelimitedTextDataset): Promise<Model.DatasetV2Resource> {
        const modelDatasetResource = await super._toResource(dataset) as Model.DatasetV2Resource;
        const modelDataset = this.properties(modelDatasetResource) as Model.ParquetDataset;
        modelDataset.type = Metamodel.modelFor(DelimitedTextDataset).name;
        modelDataset.typeProperties = modelDataset.typeProperties || {};
        if (dataset.location) {
            modelDataset.typeProperties.location = this.locationToResource(dataset);
        }
        const typeProps = modelDataset.typeProperties as Model.DelimitedTextDatasetTypeProperties;
        // TODO: fix this hack by actually passing in an unescaped delimiter
        const columnDelimiter = ValueAdapter.toResource(dataset, DelimitedTextDataset.COLUMN_DELIMITER);
        try {
            typeProps.columnDelimiter = JSON.parse(JSON.stringify(columnDelimiter).replace(/\\\\/g, '\\'));
        } catch {
            typeProps.columnDelimiter = columnDelimiter;
        }
        if (typeProps.columnDelimiter != null && typeof typeProps.columnDelimiter === 'string') {
            typeProps.columnDelimiter = string2UnicodeSingle(typeProps.columnDelimiter);
        }
        // do same hack for row delimiter, customer can manually input \r, \n, without being escaped as \\r, \\n
        const rowDelimiter = ValueAdapter.toResource(dataset, DelimitedTextDataset.ROW_DELIMITER);
        try {
            typeProps.rowDelimiter = JSON.parse(JSON.stringify(rowDelimiter).replace(/\\\\/g, '\\'));
        } catch {
            typeProps.rowDelimiter = rowDelimiter;
        }
        if (typeProps.rowDelimiter != null && typeof typeProps.rowDelimiter === 'string') {
            typeProps.rowDelimiter = string2UnicodeSingle(typeProps.rowDelimiter);
        }

        typeProps.compressionCodec = ValueAdapter.toResource(dataset, DelimitedTextDataset.COMPRESSION_CODEC);
        if (typeProps.compressionCodec !== undefined) {
            typeProps.compressionLevel = ValueAdapter.toResource(dataset, DelimitedTextDataset.COMPRESSION_LEVEL);
        }
        typeProps.encodingName = ValueAdapter.toResource(dataset, DelimitedTextDataset.ENCODING_NAME);
        typeProps.escapeChar = ValueAdapter.toResource(dataset, DelimitedTextDataset.ESCAPE_CHAR);
        if (typeProps.escapeChar != null && typeof typeProps.escapeChar === 'string') {
            typeProps.escapeChar = string2UnicodeSingle(typeProps.escapeChar);
        }

        typeProps.firstRowAsHeader = ValueAdapter.toResource(dataset, DelimitedTextDataset.FIRSTROW_ASHEADER);
        typeProps.nullValue = ValueAdapter.toResource(dataset, DelimitedTextDataset.NULL_VALUE) || undefined;
        typeProps.quoteChar = ValueAdapter.toResource(dataset, DelimitedTextDataset.QUOTE_CHAR);
        if (typeProps.quoteChar != null && typeof typeProps.quoteChar === 'string') {
            typeProps.quoteChar = string2UnicodeSingle(typeProps.quoteChar);
        }

        if (__IS_FABRIC_DPE__) {
            typeProps.multiline = ValueAdapter.toResource(dataset, DelimitedTextDataset.MULTILINE);
        }

        modelDataset.schema = this.columnValueToResource(dataset, dataset.columns);
        if (dataset.raw) {
            const parsed = JSON.parse(dataset.raw);
            _.merge(modelDatasetResource, parsed);
        }
        return modelDatasetResource;
    }

    protected async _toModel(modelDatasetResource: Model.DatasetV2Resource, dataset?: DelimitedTextDataset): Promise<DelimitedTextDataset> {
        if (!dataset) {
            dataset = DelimitedTextDataset.MODEL.create<DelimitedTextDataset>(modelDatasetResource.name);
        }
        await super._toModel(modelDatasetResource, dataset);
        if (modelDatasetResource
            && modelDatasetResource.properties
            && modelDatasetResource.properties.typeProperties
            && modelDatasetResource.properties.typeProperties.location) {
            await this.locationToModel(modelDatasetResource, dataset);
        }

        if (modelDatasetResource.properties && modelDatasetResource.properties.typeProperties) {
            const typeProps = modelDatasetResource.properties.typeProperties as Model.DelimitedTextDatasetTypeProperties;
            ValueAdapter.toModel(dataset, DelimitedTextDataset.COLUMN_DELIMITER, typeProps.columnDelimiter);
            if (dataset.columnDelimiter != null && typeof dataset.columnDelimiter === 'string') {
                dataset.columnDelimiter = unicode2StrSingle(dataset.columnDelimiter, columnDelimiterV2Options.map((option) => option.value));
                if (!dataset.columnDelimiter.startsWith('\\u')) {
                    // reserve \\ to avoid second escape
                    dataset.columnDelimiter = dataset.columnDelimiter.replace(/\\/g, '\\\\');
                }
            } else if (DelimitedTextDataset.COLUMN_DELIMITER.templateValue(dataset)) {
                let templateValue: string;
                try {
                    templateValue = JSON.parse(
                        JSON.stringify(
                            DelimitedTextDataset.COLUMN_DELIMITER.templateValue(dataset)
                        ).replace(/\\/g, '\\\\')
                    );
                } catch {
                    templateValue = DelimitedTextDataset.COLUMN_DELIMITER.templateValue(dataset);
                }
                DelimitedTextDataset.COLUMN_DELIMITER.setTemplateValue(dataset, templateValue);
            }

            if (typeProps.rowDelimiter == null) {
                // undefined means default row delimiter
                dataset.rowDelimiter = undefined;
            } else {
                ValueAdapter.toModel(dataset, DelimitedTextDataset.ROW_DELIMITER, typeProps.rowDelimiter);
            }
            if (dataset.rowDelimiter != null && typeof dataset.rowDelimiter === 'string') {
                dataset.rowDelimiter = unicode2StrSingle(dataset.rowDelimiter, rowDelimiterV2Options.map((option) => option.value));
                if (!dataset.rowDelimiter.startsWith('\\u')) {
                    dataset.rowDelimiter = dataset.rowDelimiter.replace(/\\/g, '\\\\');
                }
            } else if (DelimitedTextDataset.ROW_DELIMITER.templateValue(dataset)) {
                let templateValue: string;
                try {
                    templateValue = JSON.parse(
                        JSON.stringify(
                            DelimitedTextDataset.ROW_DELIMITER.templateValue(dataset)
                        ).replace(/\\/g, '\\\\')
                    );
                } catch {
                    templateValue = DelimitedTextDataset.ROW_DELIMITER.templateValue(dataset);
                }
                DelimitedTextDataset.ROW_DELIMITER.setTemplateValue(dataset, templateValue);
            }
            ValueAdapter.toModel(dataset, DelimitedTextDataset.COMPRESSION_CODEC, typeProps.compressionCodec);
            ValueAdapter.toModel(dataset, DelimitedTextDataset.COMPRESSION_LEVEL, typeProps.compressionLevel);
            ValueAdapter.toModel(dataset, DelimitedTextDataset.ENCODING_NAME, typeProps.encodingName);
            ValueAdapter.toModel(dataset, DelimitedTextDataset.ESCAPE_CHAR, typeProps.escapeChar);
            if (dataset.escapeChar != null && typeof dataset.escapeChar === 'string') {
                dataset.escapeChar = unicode2StrSingle(dataset.escapeChar, escapeCharV2Options.map((option) => option.value));
            }

            if (typeProps.firstRowAsHeader !== undefined) {
                ValueAdapter.toModel(dataset, DelimitedTextDataset.FIRSTROW_ASHEADER, typeProps.firstRowAsHeader);
            } else {
                delete dataset.firstRowAsHeader;
            }
            ValueAdapter.toModel(dataset, DelimitedTextDataset.NULL_VALUE, typeProps.nullValue);
            ValueAdapter.toModel(dataset, DelimitedTextDataset.QUOTE_CHAR, typeProps.quoteChar);
            if (dataset.quoteChar != null && typeof dataset.quoteChar === 'string') {
                dataset.quoteChar = unicode2StrSingle(dataset.quoteChar, quoteCharV2Options.map((option) => option.value));
            }

            if (__IS_FABRIC_DPE__) {
                ValueAdapter.toModel(dataset, DelimitedTextDataset.MULTILINE, typeProps.multiline);
            }
        }

        dataset.columns = this.columnResourceToValue(dataset, modelDatasetResource.properties.schema);

        dataset.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelDatasetResource, DelimitedTextDataset.MODEL));
        return dataset;
    }
}
